import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Sparkles, Loader, ArrowRight, Check, AlertCircle } from 'lucide-react';
import { useAuth, useUser, SignInButton } from '@clerk/clerk-react';
import { apiCallJson } from '../utils/api';
import { useTheme } from '../context/ThemeContext';
import { useToast } from './Toast';

/**
 * Affiliate sign-up form
 * ----------------------
 *
 * Public marketing pages link here (`app.bidoryx.com/affiliate/signup`).
 *
 * Flow:
 *   1. User must be signed in via Clerk. If they aren't, we show the
 *      Clerk sign-in CTA — same email they sign up with becomes the
 *      affiliate email.
 *   2. Once authenticated we show the form (display name + payout
 *      details + ToS accept).
 *   3. POST /api/affiliate/signup → server returns the new pending row.
 *   4. Show success state + link to the affiliate dashboard, where the
 *      user sees the "application pending" message until an admin
 *      approves them.
 */

export default function AffiliateSignup() {
  const { theme } = useTheme();
  const toast = useToast();
  const navigate = useNavigate();
  const { isLoaded, isSignedIn } = useAuth();
  const { user } = useUser();

  const [form, setForm] = useState({
    displayName: '',
    payoutHolder: '',
    payoutIban: '',
    payoutBic: '',
    payoutCountry: '',
    vatNumber: '',
    acceptTerms: false
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [existing, setExisting] = useState(null);

  // Pre-fill display name + check if already signed up.
  useEffect(() => {
    if (!isSignedIn) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiCallJson('/api/affiliate/me');
        if (cancelled) return;
        if (res?.success && res.affiliate) {
          setExisting(res.affiliate);
        } else if (user?.fullName) {
          setForm(prev => ({ ...prev, displayName: user.fullName }));
        }
      } catch (e) { /* not signed up yet — that's fine */ }
    })();
    return () => { cancelled = true; };
  }, [isSignedIn, user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.displayName.trim()) {
      toast?.error('Please enter a display name');
      return;
    }
    if (!form.acceptTerms) {
      toast?.error('You must accept the affiliate terms to continue');
      return;
    }

    setSubmitting(true);
    try {
      const res = await apiCallJson('/api/affiliate/signup', {
        method: 'POST',
        body: {
          displayName: form.displayName.trim(),
          payoutHolder: form.payoutHolder.trim() || null,
          payoutIban: form.payoutIban.trim().replace(/\s+/g, '').toUpperCase() || null,
          payoutBic: form.payoutBic.trim().toUpperCase() || null,
          payoutCountry: form.payoutCountry.trim().toUpperCase() || null,
          vatNumber: form.vatNumber.trim() || null,
          acceptTerms: true
        }
      });
      if (!res?.success) throw new Error(res?.error || 'Failed to sign up');
      setSuccess(res.affiliate);
    } catch (err) {
      toast?.error(err.message || 'Sign-up failed');
    } finally {
      setSubmitting(false);
    }
  };

  // Outer wrapper so the layout looks the same in every state.
  const Wrapper = ({ children }) => (
    <div style={{
      minHeight: '100vh',
      background: theme.colors.bg.primary,
      color: theme.colors.text.primary,
      padding: '40px 20px',
      display: 'flex', justifyContent: 'center', alignItems: 'flex-start'
    }}>
      <div style={{ width: '100%', maxWidth: 640 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24, color: theme.colors.text.tertiary, fontSize: 13 }}>
          <Sparkles size={16} color="#8b5cf6" />
          Bidoryx Affiliate Program
        </div>
        {children}
      </div>
    </div>
  );

  if (!isLoaded) {
    return <Wrapper><div style={{ padding: 40, textAlign: 'center' }}><Loader size={20} /></div></Wrapper>;
  }

  // Not signed in → Clerk sign-in
  if (!isSignedIn) {
    return (
      <Wrapper>
        <div style={{
          background: theme.colors.bg.secondary,
          border: `1px solid ${theme.colors.border.primary}`,
          borderRadius: 12, padding: 28
        }}>
          <h1 style={{ fontSize: 26, fontWeight: 700, margin: '0 0 12px', color: theme.colors.text.primary }}>
            Apply to become an affiliate
          </h1>
          <p style={{ color: theme.colors.text.secondary, fontSize: 15, lineHeight: 1.6, marginBottom: 20 }}>
            Earn <strong style={{ color: theme.colors.text.primary }}>22% recurring commission</strong> for every paying customer you refer to Bidoryx.
            Sign in with the email you want to use as your affiliate account, then complete a short form.
          </p>
          <SignInButton mode="modal" forceRedirectUrl="/affiliate/signup">
            <button style={{
              background: '#6366f1', color: 'white', border: 'none',
              borderRadius: 10, padding: '12px 22px', fontSize: 15, fontWeight: 600,
              cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8
            }}>
              Sign in to continue <ArrowRight size={16} />
            </button>
          </SignInButton>
          <div style={{ marginTop: 14, fontSize: 13, color: theme.colors.text.tertiary }}>
            Already an affiliate? <Link to="/affiliate-dashboard" style={{ color: '#8b5cf6', textDecoration: 'none' }}>Open your dashboard</Link>
          </div>
        </div>
      </Wrapper>
    );
  }

  // Already signed up
  if (existing) {
    return (
      <Wrapper>
        <div style={{
          background: theme.colors.bg.secondary,
          border: `1px solid ${theme.colors.border.primary}`,
          borderRadius: 12, padding: 28
        }}>
          <Check size={36} color="#10b981" style={{ marginBottom: 12 }} />
          <h1 style={{ fontSize: 24, fontWeight: 700, margin: '0 0 10px', color: theme.colors.text.primary }}>
            You're already signed up
          </h1>
          <p style={{ color: theme.colors.text.secondary, fontSize: 15, lineHeight: 1.6, marginBottom: 18 }}>
            Your affiliate status is currently <strong style={{ color: theme.colors.text.primary }}>{existing.status}</strong>.
            {existing.status === 'pending' && ' We typically respond within 1–2 business days.'}
          </p>
          <button
            onClick={() => navigate('/affiliate-dashboard')}
            style={{
              background: '#6366f1', color: 'white', border: 'none',
              borderRadius: 10, padding: '10px 20px', fontSize: 14, fontWeight: 600,
              cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8
            }}
          >
            Open dashboard <ArrowRight size={16} />
          </button>
        </div>
      </Wrapper>
    );
  }

  // Just submitted — show confirmation
  if (success) {
    return (
      <Wrapper>
        <div style={{
          background: theme.colors.bg.secondary,
          border: `1px solid #10b981`,
          borderRadius: 12, padding: 28
        }}>
          <div style={{
            width: 56, height: 56, borderRadius: '50%', background: '#10b9811f',
            display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14
          }}>
            <Check size={28} color="#10b981" />
          </div>
          <h1 style={{ fontSize: 24, fontWeight: 700, margin: '0 0 10px', color: theme.colors.text.primary }}>
            Application received
          </h1>
          <p style={{ color: theme.colors.text.secondary, fontSize: 15, lineHeight: 1.7, marginBottom: 18 }}>
            Thanks {success.displayName ? `, ${success.displayName}` : ''}. We've received your application
            and will review it within 1–2 business days. You'll get an email and an in-app notification
            as soon as we approve you. Until then your dashboard shows the pending state.
          </p>
          <button
            onClick={() => navigate('/affiliate-dashboard')}
            style={{
              background: '#6366f1', color: 'white', border: 'none',
              borderRadius: 10, padding: '10px 20px', fontSize: 14, fontWeight: 600,
              cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8
            }}
          >
            Open dashboard <ArrowRight size={16} />
          </button>
        </div>
      </Wrapper>
    );
  }

  // Main form
  const inputStyle = {
    width: '100%', boxSizing: 'border-box',
    background: theme.colors.bg.primary,
    color: theme.colors.text.primary,
    border: `1px solid ${theme.colors.border.primary}`,
    borderRadius: 8, padding: '10px 12px', fontSize: 14
  };
  const labelStyle = {
    display: 'block', fontSize: 12, fontWeight: 600,
    marginBottom: 6, color: theme.colors.text.secondary,
    textTransform: 'uppercase', letterSpacing: '0.04em'
  };

  return (
    <Wrapper>
      <h1 style={{ fontSize: 28, fontWeight: 700, margin: '0 0 8px', color: theme.colors.text.primary }}>
        Apply to become an affiliate
      </h1>
      <p style={{ color: theme.colors.text.secondary, fontSize: 14, lineHeight: 1.6, marginBottom: 22 }}>
        Earn <strong>22% recurring commission</strong> on every paid invoice from customers you refer, with a 60-day cookie window.
        Payouts are made monthly via SEPA bank transfer once your balance reaches €100.
      </p>

      <form onSubmit={submit} style={{
        background: theme.colors.bg.secondary,
        border: `1px solid ${theme.colors.border.primary}`,
        borderRadius: 12, padding: 24
      }}>
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Display name *</label>
          <input
            value={form.displayName}
            onChange={(e) => setForm({ ...form, displayName: e.target.value })}
            placeholder="Your name or company name"
            style={inputStyle}
            required
          />
          <div style={{ marginTop: 4, fontSize: 12, color: theme.colors.text.tertiary }}>
            Shown on your dashboard and to admins. You can change it later.
          </div>
        </div>

        <div style={{ borderTop: `1px solid ${theme.colors.border.primary}`, marginTop: 8, paddingTop: 16, marginBottom: 16 }}>
          <div style={{ color: theme.colors.text.primary, fontSize: 14, fontWeight: 700, marginBottom: 4 }}>
            Payout details
          </div>
          <div style={{ color: theme.colors.text.tertiary, fontSize: 12, marginBottom: 14 }}>
            Optional now — you can add these before your first payout. We pay via SEPA bank transfer.
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={labelStyle}>Account holder</label>
              <input
                value={form.payoutHolder}
                onChange={(e) => setForm({ ...form, payoutHolder: e.target.value })}
                placeholder="Name as it appears on your bank account"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Country</label>
              <input
                value={form.payoutCountry}
                onChange={(e) => setForm({ ...form, payoutCountry: e.target.value })}
                placeholder="NL"
                maxLength={2}
                style={inputStyle}
              />
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>IBAN</label>
            <input
              value={form.payoutIban}
              onChange={(e) => setForm({ ...form, payoutIban: e.target.value })}
              placeholder="NL91 ABNA 0417 1643 00"
              style={{ ...inputStyle, fontFamily: 'monospace' }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>BIC (optional)</label>
              <input
                value={form.payoutBic}
                onChange={(e) => setForm({ ...form, payoutBic: e.target.value })}
                placeholder="ABNANL2A"
                style={{ ...inputStyle, fontFamily: 'monospace' }}
              />
            </div>
            <div>
              <label style={labelStyle}>VAT number (optional)</label>
              <input
                value={form.vatNumber}
                onChange={(e) => setForm({ ...form, vatNumber: e.target.value })}
                placeholder="NL123456789B01"
                style={inputStyle}
              />
            </div>
          </div>
        </div>

        <div style={{
          background: theme.colors.bg.tertiary,
          borderRadius: 8, padding: 14, marginBottom: 18
        }}>
          <label style={{ display: 'flex', gap: 10, cursor: 'pointer', alignItems: 'flex-start' }}>
            <input
              type="checkbox"
              checked={form.acceptTerms}
              onChange={(e) => setForm({ ...form, acceptTerms: e.target.checked })}
              style={{ marginTop: 4 }}
            />
            <span style={{ fontSize: 13, color: theme.colors.text.secondary, lineHeight: 1.5 }}>
              I agree to the affiliate program rules: I won't refer myself, won't engage in paid-search bidding on
              Bidoryx-branded keywords, accept that commissions are reversed when a refund is issued, and understand
              that payouts above €100 are processed monthly via SEPA bank transfer.
              {' '}
              <em style={{ color: theme.colors.text.tertiary }}>
                Full terms link will be provided after approval.
              </em>
            </span>
          </label>
        </div>

        <div style={{
          display: 'flex', gap: 10, padding: '12px 14px', borderRadius: 8,
          background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.25)',
          fontSize: 12, color: theme.colors.text.secondary, marginBottom: 18
        }}>
          <AlertCircle size={16} color="#8b5cf6" style={{ flexShrink: 0, marginTop: 1 }} />
          <div>
            All applications are reviewed manually. We typically respond within 1–2 business days. Tell us about
            your audience by replying to the confirmation email so we can speed things up.
          </div>
        </div>

        <button
          type="submit"
          disabled={submitting || !form.acceptTerms || !form.displayName.trim()}
          style={{
            background: submitting ? theme.colors.bg.tertiary : '#6366f1',
            color: 'white', border: 'none',
            borderRadius: 10, padding: '12px 22px', fontSize: 15, fontWeight: 600,
            cursor: submitting || !form.acceptTerms ? 'not-allowed' : 'pointer',
            opacity: submitting || !form.acceptTerms ? 0.6 : 1,
            display: 'inline-flex', alignItems: 'center', gap: 8
          }}
        >
          {submitting ? (<><Loader size={16} /> Submitting…</>) : (<>Submit application <ArrowRight size={16} /></>)}
        </button>
      </form>
    </Wrapper>
  );
}
